import { useEffect, useState, type ReactNode } from 'react'
import { ExternalLink, RefreshCw, Unplug } from 'lucide-react'
import { api, type GoogleStatus } from '@/api/client'
import {
  useGoogleConnect,
  useGoogleDisconnect,
  useGoogleStatus,
  useSetAdmobAccount,
  useSetAdsConfig,
  useSyncNow,
  useSyncRuns,
} from '@/lib/queries'
import { admobKey, adsError, spendState } from '@/lib/spend'
import {
  Button,
  Code,
  ConfirmDialog,
  Disclosure,
  ErrorState,
  Field,
  FieldGroup,
  Input,
  KeyValue,
  Notice,
  PageHeader,
  Skeleton,
  StatusPill,
  When,
  errorText,
  useToast,
} from '@/ui'

/** A card with a title: one section of the page. */
function Section({ title, description, actions, children }: { title: string; description?: ReactNode; actions?: ReactNode; children: ReactNode }) {
  return (
    <section aria-label={title} className="rounded-lg border border-border bg-card">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <h2 className="text-section font-semibold">{title}</h2>
          {description && <p className="mt-0.5 text-meta text-muted-foreground">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </header>
      <div className="space-y-4 px-4 py-4">{children}</div>
    </section>
  )
}

function FormError({ error }: { error: unknown }) {
  if (!error) return null
  return (
    <p role="alert" className="whitespace-pre-wrap break-words font-mono text-meta text-status-failed">
      {errorText(error)}
    </p>
  )
}

export default function GooglePage() {
  const status = useGoogleStatus()
  const sync = useSyncRuns()
  const toast = useToast()

  const [code, setCode] = useState('')
  const [pubId, setPubId] = useState('')
  const [adsCustomer, setAdsCustomer] = useState('')
  const [adsDevToken, setAdsDevToken] = useState('')
  const [adsLoginCustomer, setAdsLoginCustomer] = useState('')
  const [admobEdit, setAdmobEdit] = useState('')
  const [confirm, setConfirm] = useState<'sync' | 'disconnect' | null>(null)

  // Google sends the operator back to /settings/google?code=…&scope=…&state=….
  // The code lands in the approval field and the three parameters leave the
  // address; nothing is sent until the operator presses 연결.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const returnedCode = params.get('code')
    if (!returnedCode) return

    setCode(returnedCode)
    params.delete('code')
    params.delete('scope')
    params.delete('state')
    const query = params.toString()
    history.replaceState(null, '', `${location.pathname}${query ? `?${query}` : ''}`)
  }, [])

  const connect = useGoogleConnect()
  const saveAds = useSetAdsConfig()
  const saveAdmob = useSetAdmobAccount()
  const syncNow = useSyncNow()
  const disconnect = useGoogleDisconnect()

  const s = status.data

  // Prefill the edit form from the server once the status lands.
  useEffect(() => {
    if (!s?.ads_linked) return
    setAdsCustomer((v) => v || s.ads_customer_id)
    setAdsLoginCustomer((v) => v || s.ads_login_customer_id)
  }, [s?.ads_linked, s?.ads_customer_id, s?.ads_login_customer_id])

  const openAuth = async (ads: boolean) => {
    try {
      const res = await api.googleAuthUrl(ads)
      window.open(res.url, '_blank')
    } catch (e) {
      toast.error(`동의 화면 주소를 받지 못했다: ${errorText(e)}`)
    }
  }

  async function runSync() {
    try {
      const r = await syncNow.mutateAsync()
      const parts = [`AdMob ${r.admob_rows ?? 0}행`, r.ads_error ? 'Google Ads 접근 거부' : `Google Ads ${r.ads_rows ?? 0}행`]
      if (r.admob_error) toast.error(`동기화는 끝났지만 AdMob이 실패했다: ${r.admob_error}`)
      else toast.success(`동기화를 마쳤다 · ${parts.join(' · ')}`)
    } catch (e) {
      toast.error(`동기화 실패: ${errorText(e)}`)
    }
    setConfirm(null)
  }

  async function runDisconnect() {
    try {
      await disconnect.mutateAsync()
      setCode('')
      toast.success('연결을 끊고 동기화된 데이터를 지웠다')
    } catch (e) {
      toast.error(`연결을 끊지 못했다: ${errorText(e)}`)
    }
    setConfirm(null)
  }

  async function submitConnect() {
    try {
      await connect.mutateAsync({
        code,
        admob_pub_id: pubId || undefined,
        ads_customer: adsCustomer || undefined,
        ads_dev_token: adsDevToken || undefined,
        ads_login_customer: adsLoginCustomer || undefined,
      })
      setCode('')
      toast.success('Google 계정을 연결했다. 앱과 캠페인을 가져오는 중이다')
    } catch {
      // the error stays under the form (connect.error)
    }
  }

  async function submitAds() {
    try {
      await saveAds.mutateAsync({ customer: adsCustomer, devToken: adsDevToken, loginCustomer: adsLoginCustomer })
      setAdsDevToken('')
      toast.success('Ads 계정을 저장했다')
    } catch {
      // saveAds.error is drawn under the form
    }
  }

  async function submitAdmob() {
    try {
      await saveAdmob.mutateAsync(admobEdit.trim())
      setAdmobEdit('')
      toast.success('AdMob 게시자 ID를 저장했다')
    } catch {
      // saveAdmob.error is drawn under the form
    }
  }

  const consentButtons = (adsOnly: boolean) => (
    <div className="flex flex-wrap gap-2">
      <Button icon={ExternalLink} onClick={() => openAuth(true)} disabled={!s?.oauth_configured}>
        Google 동의 열기 (AdMob + Ads)
      </Button>
      {!adsOnly && (
        <Button icon={ExternalLink} onClick={() => openAuth(false)} disabled={!s?.oauth_configured}>
          Google 동의 열기 (AdMob만)
        </Button>
      )}
    </div>
  )

  const oauthMissing = s && !s.oauth_configured && (
    <Notice tone="publishing" title="Google OAuth 설정이 서버에 없다">
      서버에 <Code>GOOGLE_CLIENT_ID</Code>, <Code>GOOGLE_CLIENT_SECRET</Code>, <Code>GOOGLE_REDIRECT_URI</Code>가 있어야 동의 화면을 열 수 있다.
    </Notice>
  )

  return (
    <>
      <PageHeader title="Google 연결" description="AdMob 수익과 Google Ads 지출을 가져오는 계정." />

      <div className="max-w-3xl space-y-5">
        {status.error ? (
          <ErrorState title="연결 상태를 불러오지 못했다" error={status.error} onRetry={() => status.refetch()} />
        ) : (
          <Section
            title="연결 상태"
            actions={
              <>
                <Button
                  variant="primary"
                  icon={RefreshCw}
                  disabled={!s?.connected || syncNow.isPending}
                  loading={syncNow.isPending}
                  onClick={() => setConfirm('sync')}
                >
                  지금 동기화
                </Button>
                <Button variant="dangerOutline" icon={Unplug} disabled={!s?.connected || disconnect.isPending} onClick={() => setConfirm('disconnect')}>
                  연결 끊기
                </Button>
              </>
            }
          >
            {!s || sync.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : (
              <>
                <StatusList s={s} runs={sync.runs} />
                <AdsRefused s={s} runs={sync.runs} />
              </>
            )}
          </Section>
        )}

        {s && !s.connected && (
          <Section title="처음 연결" description="Google 동의를 마치면 이 화면으로 돌아오고, 승인 코드가 자동으로 들어온다.">
            {consentButtons(false)}
            {oauthMissing}
            <FieldGroup columns={2}>
              <Field label="승인 코드" required>
                <Input className="font-mono" placeholder="4/0AX4X···" value={code} onChange={(e) => setCode(e.target.value)} />
              </Field>
              <Field label="AdMob 게시자 ID" hint="pub-로 시작한다. 나중에 바꿀 수 있다.">
                <Input className="font-mono" placeholder="pub-1234567890" value={pubId} onChange={(e) => setPubId(e.target.value)} />
              </Field>
            </FieldGroup>
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="primary" disabled={code === ''} loading={connect.isPending} onClick={submitConnect}>
                연결
              </Button>
            </div>
            <FormError error={connect.error} />
            <Disclosure title="도움말">
              <p className="text-table text-muted-foreground">
                버튼으로 Google 동의 화면을 열고 동의를 마친다. 이 화면으로 돌아오면 승인 코드가 자동으로 들어온다. AdMob은 AdMob 계정으로 로그인한 Google 계정이어야 한다.
              </p>
            </Disclosure>
          </Section>
        )}

        {s?.connected && !s.ads_linked && (
          <Section title="Ads 연결" description="광고 지출 추적에 필요하다. 연결하지 않으면 수익만 들어온다.">
            {consentButtons(true)}
            {oauthMissing}
            <FieldGroup columns={2}>
              <Field label="승인 코드" required hint="Ads 범위로 동의해 새로 받은 코드">
                <Input className="font-mono" placeholder="4/0AX4X···" value={code} onChange={(e) => setCode(e.target.value)} />
              </Field>
              <Field label="Ads 고객 ID" required>
                <Input className="font-mono" placeholder="123-456-7890" value={adsCustomer} onChange={(e) => setAdsCustomer(e.target.value)} />
              </Field>
              <Field label="개발자 토큰" required>
                <Input type="password" autoComplete="off" className="font-mono" placeholder="1a2b3c···" value={adsDevToken} onChange={(e) => setAdsDevToken(e.target.value)} />
              </Field>
              <Field label="MCC ID (관리자 계정)" hint="선택">
                <Input className="font-mono" placeholder="987-654-3210" value={adsLoginCustomer} onChange={(e) => setAdsLoginCustomer(e.target.value)} />
              </Field>
            </FieldGroup>
            <Button
              variant="primary"
              disabled={code === '' || adsCustomer === '' || adsDevToken === ''}
              loading={connect.isPending}
              onClick={submitConnect}
            >
              Ads 연결
            </Button>
            <FormError error={connect.error} />
            <Disclosure title="도움말">
              <p className="text-table text-muted-foreground">
                광고 범위로 다시 동의해야 하므로 위 버튼으로 새 코드를 받아 함께 제출한다. 개발자 토큰이 아직 없으면 나중에 추가해도 된다.
              </p>
              <p className="text-table text-muted-foreground">개발자 토큰은 Google Ads → 도구 및 설정 → 설정 → API 센터에서 확인한다.</p>
            </Disclosure>
          </Section>
        )}

        {s?.connected && (
          <Section title="AdMob 계정" description="수익을 읽어 올 게시자 ID.">
            <KeyValue items={[{ label: '게시자 ID', value: s.admob_account_id || '미설정', mono: !!s.admob_account_id }]} />
            <Field label={s.admob_account_id ? '게시자 ID 바꾸기' : '게시자 ID'} hint="pub-로 시작한다">
              <Input className="font-mono" placeholder="pub-1234567890" value={admobEdit} onChange={(e) => setAdmobEdit(e.target.value)} />
            </Field>
            <Button disabled={admobEdit.trim() === ''} loading={saveAdmob.isPending} onClick={submitAdmob}>
              저장
            </Button>
            <FormError error={saveAdmob.error} />
          </Section>
        )}

        {s?.ads_linked && (
          <Section title="Ads 계정" description="연결을 끊지 않고 여기서 바꾼다.">
            <FieldGroup columns={2}>
              <Field label="Ads 고객 ID">
                <Input className="font-mono" placeholder="123-456-7890" value={adsCustomer} onChange={(e) => setAdsCustomer(e.target.value)} />
              </Field>
              <Field label="MCC ID (관리자 계정)" hint="계정이 관리자 하위에 있으면 필요하다. 비면 Google이 PERMISSION_DENIED로 거절할 수 있다.">
                <Input className="font-mono" placeholder="987-654-3210" value={adsLoginCustomer} onChange={(e) => setAdsLoginCustomer(e.target.value)} />
              </Field>
              <Field
                className="sm:col-span-2"
                label="개발자 토큰"
                hint={s.ads_developer_token_set ? '저장됨. 저장할 때 서버가 두 값을 함께 받으므로 다시 입력한다.' : '아직 저장하지 않았다.'}
              >
                <Input type="password" autoComplete="off" className="font-mono" placeholder="1a2b3c···" value={adsDevToken} onChange={(e) => setAdsDevToken(e.target.value)} />
              </Field>
            </FieldGroup>
            <Button disabled={adsCustomer === '' || adsDevToken === ''} loading={saveAds.isPending} onClick={submitAds}>
              저장
            </Button>
            <FormError error={saveAds.error} />
          </Section>
        )}
      </div>

      <ConfirmDialog
        open={confirm === 'sync'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="지금 동기화할까"
        description="AdMob과 Google Ads API를 바로 부른다. 수십 초 걸리고, 끝나면 모든 숫자를 다시 읽는다."
        confirmLabel="동기화"
        onConfirm={runSync}
      />
      <ConfirmDialog
        open={confirm === 'disconnect'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="연결을 끊을까"
        description="연결을 끊고 동기화된 데이터를 모두 삭제할까요? 처음부터 다시 연결합니다."
        confirmLabel="연결 끊기"
        confirmVariant="destructive"
        onConfirm={runDisconnect}
      />
    </>
  )
}

/** Google Ads linked but the last pass was refused (403) or failed: what
 *  came back, when, and what happens next. Same words as 오늘 and 캠페인. */
function AdsRefused({ s, runs }: { s: GoogleStatus; runs: Parameters<typeof admobKey>[0]['runs'] }) {
  const state = spendState({ status: s, runs })
  if (state !== 'blocked' && state !== 'error') return null
  const err = adsError(runs)
  return (
    <Notice
      tone="failed"
      title={state === 'blocked' ? 'Google Ads 접근 거부 — 지출이 들어오지 않는다' : 'Google Ads 조회 오류 — 지출이 들어오지 않는다'}
      detail={err?.message}
    >
      {err?.at && (
        <>
          마지막 시도 <When iso={err.at} mode="absolute" />.{' '}
        </>
      )}
      {state === 'blocked' ? 'Google Cloud 프로젝트의 Ads API 프로덕션 승인이 필요하다.' : '다음 동기화에서 다시 시도한다.'}
    </Notice>
  )
}

function StatusList({ s, runs }: { s: GoogleStatus; runs: Parameters<typeof admobKey>[0]['runs'] }) {
  const admob = admobKey({ status: s, runs })
  // The Ads row says what the spend does (정상 · 접근 거부 · 캠페인 없음), as
  // 오늘 and 캠페인 do — a linked account that Google refuses is not 연결됨.
  const spend = spendState({ status: s, runs })
  return (
    <KeyValue
      items={[
        { label: 'Google 계정', value: <StatusPill domain="google" status={s.connected ? 'connected' : 'offline'} /> },
        {
          label: 'AdMob',
          value: (
            <span className="inline-flex flex-wrap items-center gap-2">
              <StatusPill domain="admob" status={admob} />
              {s.admob_account_id && <Code>{s.admob_account_id}</Code>}
            </span>
          ),
        },
        {
          label: 'Google Ads',
          value: s.ads_linked ? (
            <span className="inline-flex flex-wrap items-center gap-2">
              <StatusPill domain="spend" status={spend} />
              <Code>{s.ads_customer_id}</Code>
              <span className="text-meta text-muted-foreground">{s.ads_login_customer_id ? `MCC ${s.ads_login_customer_id}` : 'MCC 미설정'}</span>
            </span>
          ) : (
            <span className="inline-flex flex-wrap items-center gap-2">
              <StatusPill domain="spend" status="unlinked" />
              <span className="text-meta text-muted-foreground">수익만 들어온다</span>
            </span>
          ),
        },
        { label: '앱', value: <span className="tabular-nums">{s.app_count}개</span> },
        { label: '캠페인', value: <span className="tabular-nums">{s.campaign_count}개</span> },
        { label: '데이터', value: <span className="tabular-nums">{s.metric_days}일</span> },
        { label: '마지막 동기화', value: s.last_sync_at ? <When iso={s.last_sync_at} mode="absolute" /> : '—' },
      ]}
    />
  )
}
